/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../../share/util/individualWorkSupportUtil';
import config from '../../../../../share/config';
import session from '../../../../../share/session';
import customProgress from "../../../../../share/components/vue/CustomProgress";
import util from '../../../../../share/util/utils';
import handler from '../../../../../share/util/apiResultValidator';
import api from '../../../../../common/api';

//利用モジュールの取り込み
const moment = require('moment');

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',//実行監視
  EXECUTE: 'execute',//実行
  SUCCESS: 'success',//成功表示
  FAILED_ORG: 'failed_org',//失敗表示
  FAILED: 'failed'//共通エラー
};

export default {
  name: 'requirementSpES004',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      progress: 0,
      progressMax: 100,
      imageCount: 0,
      isInitialized: true,
      uuid: '',
      modal_special_requirement_ES4: false
    };
  },


  components: {
    'custom-Progress': customProgress,
  },
  props: {
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED_ORG:
        case PHASE.FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.START:
        default:
          return 'secondary';
      }
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_special_requirement_ES4 = true;
    },

    /**
     * 終了処理
     */
    async exitProcess() {
      if (this.isInitialized) {
        // 通信を1度でも行った場合、exitLogic を呼ぶ
        this.isInitialized = false;
        await this.exitLogic();
      } else {
        // Wizardを閉じる
        this.closeWizard();
      }
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_special_requirement_ES4 = false;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** 終了処理（要求） */
    async exitLogic() {
      try {
        // スピナー表示
        this.isLoading = true;

        // EUCキーの取得
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        // 終了処理 (要求)（バックエンドAPI_POST） Getのポーリング処理も行う
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'terminate',
            is_special: true
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // ポーリングが正常に完了した場合
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // Wizardを閉じる
              this.closeWizard();
              break;
            case config.STATUS_WSP_FAILED:
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      if (this.phase === PHASE.FAILED) {
        this.closeWizard();
        return;
      }
      // 異常終了：エラー表示
      this.phase = PHASE.FAILED;
    },

    /** カメラ画像出力 YES */
    async standbyProcess() {
      try {
        // NonDefaultSession遷移
        this.isLoading = true;
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        // データ読み出し(要求)（バックエンドAPI_POST）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'standby',
            is_special: true
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );
        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了
              this.phase = PHASE.EXECUTE;
              this.readImageData();
              break;
            case config.STATUS_WSP_FAILED:
            default:
              //共通エラー
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 対象画像データの読み出し */
    async readImageData() {
      //プログレスバー初期化
      this.progress = 0;
      this.progressMax = 100;
      this.imageCount = 0;

      this.startProcess();
    },

    /** 画像読出し */
    async startProcess() {
      try {
        // NonDefaultSession遷移
        this.isLoading = true;
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        // データ読み出し(要求)（バックエンドAPI_POST）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'start',
            is_special: true,
            value: this.imageCount,
            uuid: this.uuid
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );
        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:

              //画面情報を更新して次の画像を読み出す
              this.progress = this.progress + 20;// プログレスバー更新
              this.uuid = response.data.user_data.uuid;// ファイル参照の為にUUIDを保持
              if (this.imageCount < 4) {
                this.imageCount = this.imageCount + 1;//読み込みイメージ数カウントアップ

                //環境変数VUE_APP_RETRY_INTERVALの時間分sleepする
                await util.sleep(process.env.VUE_APP_RETRY_INTERVAL);

                this.startProcess();
              } else {
                //全データ取得完了
                this.fileOutput(response.data.user_data.value);
              }
              // 正常終了
              break;
            case config.STATUS_WSP_FAILED:
            default:
              this.phase = PHASE.FAILED_ORG;
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 
     * ファイル出力
     * @param {string} filePath ファイルパス
     * */
    async fileOutput(filePath) {
      try {
        this.isLoading = true;

        // ダウンロード元のファイルパス
        const params = {
          file_path: filePath
        };
        // ダウンロードURL処理
        const response = await api.getParamCall(config.WSP, '/downloadUrl', params);
        // ダウンロードURL処理のResponseをチェックする
        handler.validate(
          handler.validateTypes.all,
          response, this, null,
          () => {// 処理が成功したときのコールバック処理
            try {
              //ファイル名を変更
              const formattedDate = moment().format("YYYYMMDDHHmmss");
              const extension = filePath.slice(filePath.lastIndexOf('.'));//「.」付き拡張子を取り出す

              //URLからファイルをダウンロード
              fetch(response.data.fileUrl)
                .then((response) => response.blob())
                .then((myBlob) => {
                  const objUrl = URL.createObjectURL(myBlob);
                  const link = document.createElement("a");
                  link.href = objUrl;
                  link.download = formattedDate + extension;
                  link.click();
                  link.remove();
                  URL.revokeObjectURL(objUrl);
                  // 正常終了：実行
                  this.phase = PHASE.SUCCESS;
                });
            } catch {
              this.phase = PHASE.FAILED_ORG;
            }
          },
          (response) => {// 処理が失敗したときのコールバック処理
            if (response.causeType === handler.causeTypes.permissionError) {
              // ユーザー権限エラーの場合、エラーダイアログを表示しログアウトする
              alert.showError(this, null, response.message, () => {
                this.logout(this);
              });
            } else {
              this.phase = PHASE.FAILED_ORG;
            }
          }, null, false
        );
      } finally {
        this.isLoading = false;
      }
    },
  },

  async mounted() {
    this.init();
  },
};